import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { cssMap as compiledCssMap } from '@compiled/react';

import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import SlideIn from '@atlaskit/motion/slide-in';
import ExitingPersistence from '@atlaskit/motion/exiting-persistence';
import ResourcedEmoji from '@atlaskit/emoji/resourced-emoji';
import type { EmojiProvider } from '@atlaskit/emoji/types';
import { Reaction as ReactionComponent } from '@atlaskit/reactions/Reaction';
import type { ReactionSummary } from '@atlaskit/reactions/reaction';
import Button from '@atlaskit/button/default/button';

import { getOptimisticImageURL } from '@confluence/fabric-emoji-support';
import type { Reaction } from '@confluence/reactions/entry-points/reactionTypes.ts';

import { getShortNameForEmojiId, RESOURCED_EMOJI_COMPACT_HEIGHT } from './emojiConfig.ts';

const styles = cssMap({
	loadingCounterPlaceholder: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: token('color.background.neutral.subtle'),
		borderRadius: token('radius.small'),
		width: '24px',
		height: '14px',
	},
	loadingEmojiPlaceholder: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: token('color.background.neutral.subtle'),
		borderRadius: token('radius.small'),
		width: '16px',
		height: '16px',
	},
	emojiContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '16px',
		height: '16px',
	},
	popupContent: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
		width: 'fit-content',
	},
	counterContainer: {
		overflow: 'hidden',
		position: 'relative',
		fontVariantNumeric: 'tabular-nums',
	},
});

const reactionsGridStyles = compiledCssMap({
	grid: {
		display: 'inline-grid',
		gap: token('space.050'),
		alignItems: 'center',
		gridTemplateColumns: 'repeat(var(--reaction-column-count, 1), auto)',
	},
});

interface EmojiItemProps {
	emojiId: string;
	emojiProvider: Promise<EmojiProvider>;
	cloudId?: string;
}

const EmojiItemInner: React.FC<EmojiItemProps> = ({ emojiId, emojiProvider, cloudId }) => {
	const emojiIdObj = useMemo(
		() => ({ id: emojiId, shortName: getShortNameForEmojiId(emojiId) }),
		[emojiId],
	);

	return (
		<Box xcss={styles.emojiContainer}>
			<ResourcedEmoji
				emojiProvider={emojiProvider}
				emojiId={emojiIdObj}
				fitToHeight={RESOURCED_EMOJI_COMPACT_HEIGHT}
				showTooltip={false}
				optimisticImageURL={cloudId ? getOptimisticImageURL(cloudId, emojiId, 'MDPI') : undefined}
			/>
		</Box>
	);
};

const EmojiItem = memo(EmojiItemInner);

interface EmojiListProps {
	isLoading: boolean;
	topReactions: Reaction[];
	emojiProvider: Promise<EmojiProvider>;
	cloudId?: string;
}

const EmojiList: React.FC<EmojiListProps> = ({
	isLoading,
	topReactions,
	emojiProvider,
	cloudId,
}) => {
	if (isLoading) {
		return <Box xcss={styles.loadingEmojiPlaceholder} />;
	}
	if (topReactions.length === 0) {
		return null;
	}
	return (
		<Inline space="space.025">
			{topReactions.map((reaction) => (
				<EmojiItem
					key={reaction.emojiId}
					emojiId={reaction.emojiId}
					emojiProvider={emojiProvider}
					cloudId={cloudId}
				/>
			))}
		</Inline>
	);
};

interface CounterProps {
	isLoading: boolean;
	totalCount: number;
}

const formatNumber = (num: number): string => {
	if (num === 0) return '';
	if (num >= 1000) {
		return `${(num / 1000).toFixed(1)}k+`;
	}
	return num.toString();
};

const Counter: React.FC<CounterProps> = ({ isLoading, totalCount }) => {
	const previousValueRef = useRef<number | undefined>(undefined);

	useEffect(() => {
		previousValueRef.current = totalCount;
	}, [totalCount]);

	const isIncreasing = useMemo(
		() => previousValueRef.current !== undefined && previousValueRef.current < totalCount,
		[totalCount],
	);

	if (isLoading) {
		return <Box xcss={styles.loadingCounterPlaceholder} />;
	}
	if (totalCount === 0) {
		return null;
	}

	const label = formatNumber(totalCount);

	return (
		<Box xcss={styles.counterContainer} testId="omni-bar-reaction-counter">
			<ExitingPersistence>
				<SlideIn
					enterFrom={isIncreasing ? 'top' : 'bottom'}
					exitTo={isIncreasing ? 'top' : 'bottom'}
					key={totalCount}
					duration="medium"
				>
					{(motion, direction) => (
						<div
							ref={motion.ref}
							style={{
								position: direction === 'exiting' ? ('absolute' as const) : undefined,
							}}
							// Required by @atlaskit/motion SlideIn for animation
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
							className={motion.className}
						>
							{label}
						</div>
					)}
				</SlideIn>
			</ExitingPersistence>
		</Box>
	);
};

interface ReactionSummaryTriggerProps {
	isOpen: boolean;
	isLoading: boolean;
	totalCount: number;
	topReactions: Reaction[];
	emojiProvider: Promise<EmojiProvider>;
	onClick: () => void;
	cloudId?: string;
}

const ReactionSummaryTriggerInner: React.ForwardRefExoticComponent<
	Omit<ReactionSummaryTriggerProps, 'ref'> & React.RefAttributes<HTMLButtonElement>
> = React.forwardRef<HTMLButtonElement, Omit<ReactionSummaryTriggerProps, 'ref'>>(
	({ isOpen, isLoading, totalCount, topReactions, emojiProvider, onClick, cloudId }, ref) => {
		const handleClick = useCallback(() => {
			if (!isLoading && totalCount > 0) {
				onClick();
			}
		}, [isLoading, totalCount, onClick]);

		const iconContent = useMemo(
			() => (
				<EmojiList
					isLoading={isLoading}
					topReactions={topReactions}
					emojiProvider={emojiProvider}
					cloudId={cloudId}
				/>
			),
			[isLoading, topReactions, emojiProvider, cloudId],
		);

		const renderIcon = useCallback(() => iconContent, [iconContent]);

		return (
			<Button
				ref={ref}
				appearance="subtle"
				onClick={handleClick}
				isSelected={isOpen}
				testId="omni-bar-reaction-summary-trigger"
				iconBefore={renderIcon}
			>
				<Counter isLoading={isLoading} totalCount={totalCount} />
			</Button>
		);
	},
);

export const ReactionSummaryTrigger = memo(
	ReactionSummaryTriggerInner,
) as React.MemoExoticComponent<typeof ReactionSummaryTriggerInner>;
ReactionSummaryTrigger.displayName = 'ReactionSummaryTrigger';

interface ReactionSummaryPopupContentProps {
	reactions: ReactionSummary[];
	emojiProvider: Promise<EmojiProvider>;
	onReactionClick: (emojiId: string) => void;
}

const MAX_COLUMNS = 5;

export const ReactionSummaryPopupContent: React.FC<ReactionSummaryPopupContentProps> = ({
	reactions,
	emojiProvider,
	onReactionClick,
}) => {
	const columnCount = Math.min(reactions.length, MAX_COLUMNS);

	return (
		<Box xcss={styles.popupContent} testId="omni-bar-reaction-summary-popup">
			<Box
				xcss={reactionsGridStyles.grid}
				style={{ '--reaction-column-count': columnCount } as React.CSSProperties}
			>
				{reactions.map((reaction) => (
					<ReactionComponent
						key={reaction.emojiId}
						reaction={reaction}
						emojiProvider={emojiProvider}
						onClick={onReactionClick}
						allowUserDialog={false}
						isViewOnly={false}
					/>
				))}
			</Box>
		</Box>
	);
};
